import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { getDefaultRowIndexColumnConfig, getRowStyle } from 'src/utils/grid/gridStyleUtils';

import { defaultEQM1010_R01Data, EQM1010_R01Dto } from './Dto_EQM1010_R01';
import { useEQM1010_R01Store } from './Store_EQM1010_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'EQM1010_R01-grid-column-state';

export const Table_EQM1010_R01 = () => {
	const gridRef = useRef<AgGridReact<EQM1010_R01Dto>>(null);

	const {
		EQM1010_R01List,
		selectedEQM1010_R01List,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
	} = useEQM1010_R01Store();

	const { compCdList } = useCommonCodeStore();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: EQM1010_R01List,
		defaultGridData: defaultEQM1010_R01Data,
		selectedRow: selectedEQM1010_R01List,
	});

	const getEQM1010_R01RowId = useCallback((params: GetRowIdParams<EQM1010_R01Dto>) => {
		return `${params.data.plcid}-${params.data.plcaddressid}-${params.data.createdatetime}`;
	}, []);

	const codeMaps = useMemo(() => ({
		comp: new Map(compCdList.map((item) => [item.compCd, item.compNm])),
	}), [compCdList]);

	const headerNames: Record<string, string> = {
		rowIndex: '',
		compCd: '사업장',
		compNm: '사업장명',
		plcid: 'PLC 번호',
		plcName: 'PLC명',
		plcaddressid: '데이터종류',
		plcAddressName: '데이터명',
		value: '수집데이터값',
		createdatetime: '수집일시',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

	const baseColumnDefs: ColDef<EQM1010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'compCd'),
		},
		{
			headerName: '사업장명', colId: 'compNm', field: 'compNm', width: 150,
			headerClass: aggridHeaderClass,
			valueGetter: (params) =>
				codeMaps.comp.get(params.data?.compCd ?? '') || params.data?.compNm || '',
			cellStyle: (p) => getCellStyle(p, 'compNm'),
		},
		{
			headerName: 'PLC 번호', colId: 'plcid', field: 'plcid', width: 90,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			cellStyle: (p) => getCellStyle(p, 'plcid'),
		},
		{
			headerName: 'PLC명', colId: 'plcName', field: 'plcName', width: 120,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'plcName'),
		},
		{
			headerName: '데이터종류', colId: 'plcaddressid', field: 'plcaddressid', width: 110,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			cellStyle: (p) => getCellStyle(p, 'plcaddressid'),
		},
		{
			headerName: '데이터명', colId: 'plcAddressName', field: 'plcAddressName', width: 140,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'plcAddressName'),
		},
		{
			headerName: '수집데이터값', colId: 'value', field: 'value', width: 110,
			headerClass: aggridHeaderClass,
			type: 'numericColumn',
			cellStyle: (p) => getCellStyle(p, 'value'),
		},
		{
			headerName: '수집일시', colId: 'createdatetime', field: 'createdatetime', width: 150,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'createdatetime'),
		},
	], [getCellStyle, codeMaps.comp]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<EQM1010_R01Dto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<EQM1010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compCd: `${EQM1010_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [EQM1010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [EQM1010_R01List, selectedEQM1010_R01List]);

	return (
		<div className="flex flex-col gap-3 h-full min-h-0">
			<GridContextWrapper<EQM1010_R01Dto>
				gridRef={gridRef}
				paddedGridData={paddedGridData}
				enableCellCalculation
				initSelected={() => {}}
				defaultRowData={defaultEQM1010_R01Data}
				baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
				defaultColumnState={defaultColumnState}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				menuItems={undefined}
			>
				<Grid_Touch<EQM1010_R01Dto>
					ref={gridRef}
					rowData={paddedGridData}
					columnDefs={columnDefs}
					getRowId={getEQM1010_R01RowId}
					onCellKeyDown={undefined}
					pinnedBottomRowData={pinnedBottomRow}
					rowClassRules={createRowClassRules<EQM1010_R01Dto>({
						idKey: 'valueIndex',
						selectedList: selectedEQM1010_R01List ?? [],
						deletedList: [],
					})}
					suppressRowClickSelection={true}
					animateRows={true}
					enableCellTextSelection={true}
					ensureDomOrder={false}
					getRowStyle={getRowStyle}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
					scrollTop={scrollTop}
					setScrollTop={setScrollTop}
				/>
			</GridContextWrapper>
		</div>
	);
};
